/** A record's outline as draft records (the tour plan §5 D7; the catalog itinerary's days,
 *  O7 / O10): rows on one or more levels, each level a record entity whose ids sit in a
 *  membership list — on the owning record for the top level (`itinerary.days` on `tour:42`
 *  or `itinerary:7`), on a row of the level above below it (`day.slots` on `day:12`).
 *
 *  - The tour: days → stops (ordered by their time: `orderBy`) → activity rows.
 *  - The catalog itinerary: days only.
 *
 *  A negative id is a row the draft creates; it reads its level's `defaults` until edited.
 *  Pure (no React): values over the draft, the tree walk, where each row sits, the saved
 *  rows the draft removed, and the outline's flat rows — what the store, the structural
 *  operations (`ops.ts`) and the `Outline` share. */
import { parseRecordKey, recordKey, type DraftState, type RecordKey } from '@/features/records/draft/changeSet';

export type Baselines = Readonly<Record<RecordKey, Readonly<Record<string, unknown>>>>;
export type Entries = DraftState['entries'];

export interface OutlineLevel {
  /** The rows' record entity (`day` → `day:12`). */
  entity: string;
  /** The membership list holding this level's row ids: on the owning record for the top
   *  level, on the parent row below it. */
  list: string;
  /** A new row's values (path → value). */
  defaults: Readonly<Record<string, unknown>>;
  /** Order the rows by this path's value (a stop's "HH:MM"; equal values keep their list
   *  order) instead of by the list — the list is then a set. */
  orderBy?: string;
}

/** The levels, outermost first. */
export type OutlineSpec = readonly OutlineLevel[];

/** The level of an entity (-1: not in the outline). */
export function levelOf(spec: OutlineSpec, entity: string): number {
  return spec.findIndex((l) => l.entity === entity);
}

/** A new row's value of a path (undefined: not an outline path). */
export function outlineDefault(spec: OutlineSpec, rec: RecordKey, path: string): unknown {
  const { entity } = parseRecordKey(rec);
  return spec.find((l) => l.entity === entity)?.defaults[path];
}

/** A path's value: the draft's (edit mode; `entries` null = view mode), else the stored
 *  one, else a new row's default. */
export function valueIn(spec: OutlineSpec, entries: Entries | null, baselines: Baselines, rec: RecordKey, path: string): unknown {
  const entry = entries?.[rec]?.[path];
  if (entry) return entry.to;
  const stored = baselines[rec]?.[path];
  if (stored !== undefined) return stored;
  return outlineDefault(spec, rec, path);
}

export function listIn(spec: OutlineSpec, entries: Entries | null, baselines: Baselines, rec: RecordKey, path: string): number[] {
  const v = valueIn(spec, entries, baselines, rec, path);
  return Array.isArray(v) ? (v as unknown[]).filter((x): x is number => typeof x === 'number') : [];
}

export interface OutlineTreeNode {
  id: number;
  /** The rows of the next level, in their order (empty on the last level). */
  children: OutlineTreeNode[];
}

function ordered(spec: OutlineSpec, entries: Entries | null, baselines: Baselines, level: number, ids: number[]): number[] {
  const { entity, orderBy } = spec[level]!;
  if (!orderBy) return ids;
  const keyOf = (id: number) => {
    const v = valueIn(spec, entries, baselines, recordKey(entity, id), orderBy);
    return typeof v === 'string' ? v : '';
  };
  return ids
    .map((id, i) => ({ id, i, k: keyOf(id) }))
    .sort((a, b) => (a.k < b.k ? -1 : a.k > b.k ? 1 : a.i - b.i))
    .map((x) => x.id);
}

function nodesOf(spec: OutlineSpec, entries: Entries | null, baselines: Baselines, parentRec: RecordKey, level: number): OutlineTreeNode[] {
  const l = spec[level];
  if (!l) return [];
  return ordered(spec, entries, baselines, level, listIn(spec, entries, baselines, parentRec, l.list)).map((id) => ({
    id,
    children: nodesOf(spec, entries, baselines, recordKey(l.entity, id), level + 1),
  }));
}

/** The outline as the draft (or, with `entries` null, the stored record) has it. */
export function outlineTree(spec: OutlineSpec, entries: Entries | null, baselines: Baselines, ownerRec: RecordKey): OutlineTreeNode[] {
  return nodesOf(spec, entries, baselines, ownerRec, 0);
}

/** Where a row sits: its parent record (the owner for the top level), its level and its
 *  place among its siblings. */
export interface OutlinePlace {
  parent: RecordKey;
  level: number;
  index: number;
}

/** Every row of a tree by its record key. */
export function outlinePlacement(spec: OutlineSpec, tree: readonly OutlineTreeNode[], ownerRec: RecordKey): Map<RecordKey, OutlinePlace> {
  const out = new Map<RecordKey, OutlinePlace>();
  const walk = (nodes: readonly OutlineTreeNode[], level: number, parent: RecordKey) => {
    const entity = spec[level]?.entity;
    if (!entity) return;
    nodes.forEach((node, index) => {
      const rec = recordKey(entity, node.id);
      out.set(rec, { parent, level, index });
      walk(node.children, level + 1, rec);
    });
  };
  walk(tree, 0, ownerRec);
  return out;
}

/** The saved rows of `parentRec`'s level-`level` list that the draft removed — not
 *  placed anywhere in its tree (`placed`: the ids of that level the draft holds). View
 *  mode (`entries` null) removes nothing. */
export function removedRows(
  spec: OutlineSpec,
  entries: Entries | null,
  baselines: Baselines,
  parentRec: RecordKey,
  level: number,
  placed: ReadonlySet<number>,
): number[] {
  const l = spec[level];
  if (!entries || !l) return [];
  const stored = baselines[parentRec]?.[l.list];
  return (Array.isArray(stored) ? (stored as number[]) : []).filter((id) => !placed.has(id));
}

/** One row of the outline as a flat list. */
export interface OutlineFlatRow {
  level: number;
  entity: string;
  id: number;
  /** The parent row's id (null on the top level). */
  parentId: number | null;
  /** 0-based place among its siblings; null for a removed row. */
  index: number | null;
  /** A saved row the draft removed (struck, restorable until the save). */
  removed: boolean;
}

/** The outline's rows down to `depth` levels: each row, then its children; in edit mode
 *  a saved row's removed children follow them, and the removed top-level rows come last.
 *  (A new row has no stored children; a removed row lists none.) */
export function outlineFlatRows(
  spec: OutlineSpec,
  entries: Entries | null,
  baselines: Baselines,
  ownerRec: RecordKey,
  depth: number = spec.length,
): OutlineFlatRow[] {
  const tree = outlineTree(spec, entries, baselines, ownerRec);
  const placed = spec.map(() => new Set<number>());
  const collect = (nodes: readonly OutlineTreeNode[], level: number) => {
    for (const n of nodes) {
      placed[level]?.add(n.id);
      collect(n.children, level + 1);
    }
  };
  collect(tree, 0);
  const rows: OutlineFlatRow[] = [];
  const walk = (nodes: readonly OutlineTreeNode[], level: number, parentId: number | null) => {
    const { entity } = spec[level]!;
    const child = level + 1 < depth ? spec[level + 1] : undefined;
    nodes.forEach((node, index) => {
      rows.push({ level, entity, id: node.id, parentId, index, removed: false });
      if (!child) return;
      walk(node.children, level + 1, node.id);
      if (node.id < 0) return;
      for (const id of removedRows(spec, entries, baselines, recordKey(entity, node.id), level + 1, placed[level + 1]!)) {
        rows.push({ level: level + 1, entity: child.entity, id, parentId: node.id, index: null, removed: true });
      }
    });
  };
  if (depth > 0 && spec.length > 0) {
    walk(tree, 0, null);
    for (const id of removedRows(spec, entries, baselines, ownerRec, 0, placed[0]!)) {
      rows.push({ level: 0, entity: spec[0]!.entity, id, parentId: null, index: null, removed: true });
    }
  }
  return rows;
}
